// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

import React from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import OpenLifterIntlProvider from "./components/translations/OpenLifterIntlProvider";

import RootContainer from "./containers/RootContainer";
import MeetSetupContainer from "./containers/MeetSetupContainer";
import RegistrationContainer from "./containers/RegistrationContainer";
import WeighinsContainer from "./containers/WeighinsContainer";
import LiftingContainer from "./containers/LiftingContainer";
import FlightOrderContainer from "./containers/FlightOrderContainer";
import ResultsContainer from "./containers/ResultsContainer";
import DebugContainer from "./containers/DebugContainer";
import AboutContainer from "./containers/AboutContainer";
import Navigation from "./components/Navigation";
import IntegratedJudgesPanelRoute from "./wrpf/judging/IntegratedJudgesPanelRoute";
import JudgeRoute from "./wrpf/judging/JudgeRoute";
import SpectatorRoute from "./wrpf/spectator/SpectatorRoute";
import AthleteStreamingRoute from "./wrpf/streaming/AthleteStreamingRoute";
import OverlayStreamingRoute from "./wrpf/streaming/OverlayStreamingRoute";
import WrpfStartContainer from "./wrpf/handoff/WrpfStartContainer";
import PlatformLiveLauncherContainer from "./wrpf/live/PlatformLiveLauncherContainer";
import RecordsRoute from "./wrpf/records/RecordsRoute";
import LabIndexRoute from "./wrpf/lab/LabIndexRoute";
import OverlayLabRoute from "./wrpf/lab/overlay/OverlayLabRoute";
import FichaLabRoute from "./wrpf/lab/ficha/FichaLabRoute";
import EnVivoLabRoute from "./wrpf/lab/en-vivo/EnVivoLabRoute";
import PlatformLiveKeepAlive from "./wrpf/live/PlatformLiveKeepAlive";
import OperatorLiveHost from "./wrpf/operation/OperatorLiveHost";

import configureStore from "./store";
import PersistFailureBanner from "./wrpf/persistence/PersistFailureBanner";
import { shouldPersistOnPath, startCrossTabSync } from "./wrpf/persistence/crossTabSync";

import { getDefaultLanguage } from "./logic/strings";

const createAppStore = () => {
  const persisted = configureStore({ language: getDefaultLanguage() });
  // Jueces, espectador, transmisión y lab no guardan: así no pisan lo que guarda la operación. Se pausa antes
  // de que termine de cargar lo guardado, para que no escriban ni siquiera al arrancar.
  if (!shouldPersistOnPath(window.location.pathname)) {
    persisted.persistor.pause();
  }
  return persisted;
};

class App extends React.Component {
  // El store se crea una sola vez: crearlo en render() armaba un store y un persistor nuevos en cada render.
  private readonly persisted = createAppStore();
  private stopCrossTabSync: (() => void) | null = null;

  componentDidMount() {
    this.stopCrossTabSync = startCrossTabSync(this.persisted.store);
  }

  componentWillUnmount() {
    this.stopCrossTabSync?.();
  }

  render() {
    const { store, persistor } = this.persisted;

    return (
      // Provider is a React component from the react-redux library.
      // Its purpose is to "provide" the given store to its child components.
      // Because the Provider wraps the whole App here, the store is global state.
      //
      // Switch iterates over its children (Routes) and renders the first one that matches the current path name
      //
      // Route takes a path and a component, and renders the given component if the current path matches the specified path.
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <OpenLifterIntlProvider>
            <BrowserRouter basename={process.env.REACT_APP_ROUTER_BASENAME}>
              <div>
                <Navigation />
                <PersistFailureBanner />
                <PlatformLiveKeepAlive />
                <OperatorLiveHost>
                  <Routes>
                    <Route path="/" element={<RootContainer />} />
                    <Route path="/meet-setup" element={<MeetSetupContainer />} />
                    <Route path="/registration" element={<RegistrationContainer />} />
                    <Route path="/weigh-ins" element={<WeighinsContainer />} />
                    <Route path="/flight-order" element={<FlightOrderContainer />} />
                    <Route path="/lifting" element={<LiftingContainer />} />
                    <Route path="/days/:day/platforms/:platformId/judges" element={<IntegratedJudgesPanelRoute />} />
                    <Route path="/days/:day/platforms/:platformId/judges/:judgePosition" element={<JudgeRoute />} />
                    <Route path="/days/:day/platforms/:platformId/spectator" element={<SpectatorRoute />} />
                    <Route
                      path="/days/:day/platforms/:platformId/streaming/athlete"
                      element={<AthleteStreamingRoute />}
                    />
                    <Route
                      path="/days/:day/platforms/:platformId/streaming/overlay"
                      element={<OverlayStreamingRoute />}
                    />
                    <Route path="/lab" element={<LabIndexRoute />} />
                    <Route path="/lab/overlay" element={<OverlayLabRoute />} />
                    <Route path="/lab/ficha" element={<FichaLabRoute />} />
                    <Route path="/lab/en-vivo" element={<EnVivoLabRoute />} />
                    <Route path="/results" element={<ResultsContainer />} />
                    <Route path="/records" element={<RecordsRoute />} />
                    <Route path="/wrpf/start" element={<WrpfStartContainer />} />
                    <Route path="/wrpf/live" element={<PlatformLiveLauncherContainer />} />
                    <Route path="/debug" element={<DebugContainer />} />
                    <Route path="/about" element={<AboutContainer />} />
                  </Routes>
                </OperatorLiveHost>
              </div>
            </BrowserRouter>
          </OpenLifterIntlProvider>
        </PersistGate>
      </Provider>
    );
  }
}

export default App;
